import React from "react";
import ReactDOM from "react-dom/client";
import AppShell from "./AppShell";
import "@radix-ui/themes/styles.css";
import "@workos-inc/widgets/styles.css";
import "./index.css";
import "./styles/floating-shell.css";
import "./styles/home-dashboard.css";
import { ConvexProvider, ConvexReactClient } from "convex/react";
import { WorkOsWidgets } from "@workos-inc/widgets";

const CONVEX_URL = import.meta.env.VITE_CONVEX_URL;
if (!CONVEX_URL) {
  throw new Error("Missing VITE_CONVEX_URL.");
}
const convex = new ConvexReactClient(CONVEX_URL);

let cachedToken: string | null = null;
let cachedTokenExpiry = 0;

function getCookie(name: string) {
  if (typeof document === "undefined") return null;
  const match = document.cookie.split("; ").find((row) => row.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.split("=")[1] ?? "") : null;
}

function decodeJwtExpiry(token: string) {
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), "=");
    const decoded = JSON.parse(atob(padded));
    return typeof decoded.exp === "number" ? decoded.exp * 1000 : null;
  } catch {
    return null;
  }
}

async function fetchAccessToken() {
  const now = Date.now();
  if (cachedToken && cachedTokenExpiry - 60_000 > now) {
    return cachedToken;
  }

  let response: Response;
  try {
    const csrfToken = getCookie("csrfToken");
    response = await fetch("/api/auth/convex-token", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
      },
      credentials: "include",
    });
  } catch {
    // Network transitions can throw here (offline, VPN changes, etc).
    // Return null so Convex auth manager can retry cleanly without uncaught errors.
    cachedToken = null;
    cachedTokenExpiry = 0;
    return null;
  }

  if (!response.ok) {
    cachedToken = null;
    cachedTokenExpiry = 0;
    return null;
  }

  const data = await response.json();
  const accessToken = data?.accessToken;
  if (typeof accessToken === "string") {
    cachedToken = accessToken;
    cachedTokenExpiry = decodeJwtExpiry(accessToken) ?? now + 60_000;
    return accessToken;
  }

  cachedToken = null;
  cachedTokenExpiry = 0;
  return null;
}

convex.setAuth(fetchAccessToken);

if (typeof window !== "undefined") {
  const host = window.location.hostname;
  if (host === "apsargus.com") {
    const url = new URL(window.location.href);
    url.hostname = "www.apsargus.com";
    window.location.replace(url.toString());
  }
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <ConvexProvider client={convex}>
      <WorkOsWidgets
        className="workos-theme-root"
        theme={{
          appearance: "dark",
          accentColor: "red",
          grayColor: "gray",
          radius: "medium",
        }}
      >
        <AppShell />
      </WorkOsWidgets>
    </ConvexProvider>
  </React.StrictMode>
); 
